import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import { SelectProps } from "../../../../shared/interfaces/select";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useEffect, useId, useMemo, useState } from "react";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CustomModal } from "../../../modals/common";

const CREATE_ACTION = '__create__';
const EDIT_ACTION = '__edit__';

const SelectAutocomplete = ({ 
  data = [], 
  error, 
  onChange, 
  value, 
  placeholder, 
  width = 300, 
  height = 55, 
  disabled = false,
  multiple = false,
  actions
}: SelectProps) => {
  const uniqueId = useId();
  const [options, setOptions] = useState<any[]>(data);
  const [inputValue, setInputValue] = useState('');
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [modalValue, setModalValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    setOptions(data);
  }, [data]);

  const getOptionLabel = useMemo(() => {
    return (option: any) => {
      if (!option) return '';
      return option.label || option.name || '';
    };
  }, []);

  const getOptionId = useMemo(() => {
    return (option: any) => {
      if (!option) return null;
      return option.id || option.value || null;
    };
  }, []);

  let selectedValue: any = multiple ? [] : null;
  
  if (multiple && Array.isArray(value)) {
    selectedValue = options.filter((option) => value.includes(getOptionId(option)));
  } else if (value) {
    if (typeof value === 'object' && value !== null && 'id' in value) {
      selectedValue = value;
    }
    else if (typeof value === 'string') {
      selectedValue = options.find(opt => opt.id === value || opt.value === value) || null;
    }
  }

  const shouldShowEditAction = Boolean(
    !disabled &&
    selectedValue &&
    actions?.edit?.onSubmit &&
    (!actions.edit.canShow || actions.edit.canShow(selectedValue))
  );

  const closeModal = () => {
    setModalMode(null);
    setModalValue('');
    setIsSubmitting(false);
  };

  const handleOpenCreateModal = (name: string) => {
    setModalMode('create');
    setModalValue(name);
  };

  const handleOpenEditModal = () => {
    if (!selectedValue) return;

    setModalMode('edit');
    setModalValue(getOptionLabel(selectedValue));
  };

  const handleActionSubmit = async () => {
    const trimmedValue = modalValue.trim();

    if (!trimmedValue) return;

    try {
      setIsSubmitting(true);

      if (modalMode === 'create' && actions?.create?.onSubmit) {
        const createdOption = await actions.create.onSubmit(trimmedValue);
        const normalizedCreatedOption = {
          ...createdOption,
          id: createdOption?.id || createdOption?.value,
          value: createdOption?.value || createdOption?.id,
          label: createdOption?.label || createdOption?.name || trimmedValue,
        };

        setOptions((currentOptions) => [...currentOptions, normalizedCreatedOption]);
        setInputValue(normalizedCreatedOption.label);
        onChange?.(normalizedCreatedOption.id || normalizedCreatedOption.value || null);
      }

      if (modalMode === 'edit' && actions?.edit?.onSubmit && selectedValue) {
        const updatedOption = await actions.edit.onSubmit(selectedValue, trimmedValue);
        const normalizedUpdatedOption = {
          ...selectedValue,
          ...updatedOption,
          id: updatedOption?.id || selectedValue?.id || updatedOption?.value,
          value: updatedOption?.value || selectedValue?.value || updatedOption?.id,
          label: updatedOption?.label || updatedOption?.name || trimmedValue,
        };

        setOptions((currentOptions) => currentOptions.map((option) => {
          const optionId = option.id || option.value;
          const updatedOptionId = normalizedUpdatedOption.id || normalizedUpdatedOption.value;

          return optionId === updatedOptionId ? normalizedUpdatedOption : option;
        }));
        setInputValue(normalizedUpdatedOption.label);
        onChange?.(normalizedUpdatedOption.id || normalizedUpdatedOption.value || null);
      }

      closeModal();
    } catch (submitError) {
      console.error('Erro ao salvar opção do select:', submitError);
      setIsSubmitting(false);
    }
  };

  const filterOptions = (availableOptions: any[], state: { inputValue: string }) => {
    const normalizedInput = state.inputValue.trim().toLowerCase();
    const filteredOptions = availableOptions.filter((option) => 
      getOptionLabel(option).toLowerCase().includes(normalizedInput)
    );

    const autocompleteOptions = [...filteredOptions];

    if (
      actions?.create?.onSubmit &&
      !disabled &&
      normalizedInput &&
      filteredOptions.length === 0
    ) {
      autocompleteOptions.push({
        id: CREATE_ACTION,
        value: CREATE_ACTION,
        label: state.inputValue.trim(),
        actionType: 'create'
      });
    }

    if (
      shouldShowEditAction &&
      (!normalizedInput || normalizedInput === getOptionLabel(selectedValue).toLowerCase())
    ) {
      autocompleteOptions.unshift({
        id: EDIT_ACTION,
        value: EDIT_ACTION,
        label: getOptionLabel(selectedValue),
        actionType: 'edit'
      });
    }

    return autocompleteOptions;
  };
  
  return (
    <Box sx={{ width: width }} key={`select-autocomplete-${uniqueId}`}>
      <Autocomplete
        disablePortal
        options={options}
        filterOptions={filterOptions}
        sx={{ 
          width: "100%", 
          backgroundColor: "#fff",
          "& .MuiInputBase-root": {
            minHeight: height,
            ...(multiple ? {
              height: "auto",
              alignItems: "flex-start",
              paddingTop: "8px",
              paddingBottom: "8px",
            } : {})
          },
          "& .MuiAutocomplete-tag": {
            maxWidth: "calc(100% - 12px)"
          }
        }}
        disabled={disabled}
        multiple={multiple}
        filterSelectedOptions={multiple}
        inputValue={inputValue}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.actionType || val.actionType) return false;

          return getOptionId(option) === getOptionId(val);
        }}
        getOptionLabel={getOptionLabel}
        onChange={(_, newValue) => {
          if (multiple) {
            const multipleValue = Array.isArray(newValue)
              ? newValue
                  .filter((option) => !option?.actionType)
                  .map((option) => getOptionId(option))
                  .filter(Boolean)
              : [];

            onChange?.(multipleValue);
            return;
          }

          if (newValue?.actionType === 'create') {
            handleOpenCreateModal(newValue.label);
            return;
          }

          if (newValue?.actionType === 'edit') {
            handleOpenEditModal();
            return;
          }

          let valueToPass = null;
          
          if (newValue) {
            valueToPass = newValue.id || newValue.value || null;
            setInputValue(getOptionLabel(newValue));
          } else {
            setInputValue('');
          }
          
          onChange?.(valueToPass);
        }}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'reset') {
            if (multiple) {
              setInputValue('');
              return;
            }

            setInputValue(selectedValue ? getOptionLabel(selectedValue) : '');
            return;
          }

          setInputValue(newInputValue);
        }}
        value={selectedValue}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={getOptionLabel(option)}
              {...getTagProps({ index })}
              key={getOptionId(option) || `${getOptionLabel(option)}-${index}`}
              size="small"
            />
          ))
        }
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          if (option.actionType === 'create') {
            return (
              <Box component="li" key={key} {...optionProps}>
                <Button
                  fullWidth
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0.5 }}
                >
                  Adicionar "{option.label}"
                </Button>
              </Box>
            );
          }

          if (option.actionType === 'edit') {
            return (
              <Box component="li" key={key} {...optionProps}>
                <Button
                  fullWidth
                  startIcon={<EditOutlinedIcon />}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0.5 }}
                >
                  {actions?.edit?.label || `Editar "${option.label}"`}
                </Button>
              </Box>
            );
          }

          return (
            <Box component="li" key={key} {...optionProps}>
              <Typography variant="body2">{getOptionLabel(option)}</Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            key={uniqueId}
            {...params}
            label={placeholder}
            error={!!error}
            className={`container-input__input ${
              error ? "container-input__input__error" : "container-input__input__default"
            }`}
            sx={multiple ? {
              "& .MuiInputLabel-root": {
                top: "4px",
              },
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "wrap",
              }
            } : undefined}
          />
        )}
      />
      {error && <span className="container-input__input__error-message">{error.toString()}</span>}

      <CustomModal
        open={Boolean(modalMode)}
        onClose={closeModal}
        title={
          modalMode === 'create'
            ? actions?.create?.modalTitle || 'Adicionar opção'
            : actions?.edit?.modalTitle || 'Editar opção'
        }
        content={
          <TextField
            autoFocus
            fullWidth
            label={placeholder}
            value={modalValue}
            onChange={(event) => setModalValue(event.target.value)}
            disabled={isSubmitting}
          />
        }
        buttons={[
          {
            id: 'cancel',
            label: 'Cancelar',
            onClick: closeModal,
            isCancel: true
          },
          {
            id: 'confirm',
            label: modalMode === 'create'
              ? actions?.create?.confirmLabel || 'Adicionar'
              : actions?.edit?.confirmLabel || 'Salvar',
            onClick: handleActionSubmit
          }
        ]}
      />
    </Box>
  );
};

export default SelectAutocomplete;
